/**
 * In-cart switch between a one-time line and a subscription.
 *
 * Wires the controls rendered by snippets/tga-line-subscribe.liquid. Uses event
 * delegation on the document so the handlers survive the cart markup being
 * morphed after every cart change. Three controls:
 *   - the collapsed one-line toggle, which shows and hides the body
 *   - the "Subscription details" tooltip, which also opens on tap (phones can't hover)
 *   - the Subscribe / switch-back buttons, which call Shopify's cart change endpoint
 *
 * On a switch it sends the line's key and the chosen selling plan id (or null to
 * go back to one-time), asks for fresh cart HTML in the same call, and morphs it
 * in place. In the drawer only the cart-items-component subtree is morphed, never
 * the dialog that hosts it, so the drawer stays open. On the cart page the whole
 * section is morphed so the shipping bar and rail outside the component re-render.
 */
import { morph } from '@theme/morph';

/**
 * @param {HTMLButtonElement} button
 * @param {number | null} sellingPlanId
 */
async function changeLine(button, sellingPlanId) {
  const inDrawer = Boolean(button.closest('dialog'));
  const host = button.closest('cart-items-component');
  const section = button.closest('.shopify-section');
  const target = inDrawer ? host : section;
  const sectionId = host?.dataset.sectionId || section?.id?.replace(/^shopify-section-/, '');

  button.disabled = true;

  try {
    const payload = {
      id: button.dataset.lineKey,
      quantity: Number(button.dataset.lineQuantity),
      selling_plan: sellingPlanId,
    };
    if (sectionId) payload.sections = [sectionId];

    const response = await fetch(window.Theme?.routes?.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);

    const data = await response.json();
    const markup = sectionId ? data.sections?.[sectionId] : null;
    if (!markup || !target) {
      // No section HTML to morph with: fall back to a reload so the cart
      // is never left showing stale state.
      window.location.reload();
      return;
    }

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const incoming = inDrawer
      ? parsed.querySelector('cart-items-component')
      : parsed.getElementById(section.id) || parsed.body.firstElementChild;

    if (incoming) {
      morph(target, incoming);
    } else {
      window.location.reload();
    }
  } catch (error) {
    console.warn('[tga-line-subscribe]', error);
    button.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const toggle = event.target.closest('[data-tga-sub-toggle]');
  if (toggle instanceof HTMLButtonElement) {
    const body = toggle.closest('[data-tga-line-sub]')?.querySelector('[data-tga-sub-body]');
    if (body) {
      const opening = body.hasAttribute('hidden');
      body.toggleAttribute('hidden', !opening);
      toggle.setAttribute('aria-expanded', String(opening));
    }
    return;
  }

  const detail = event.target.closest('[data-tga-sub-detail]');
  if (detail) {
    detail.classList.toggle('is-open');
    return;
  }

  const switchButton = event.target.closest('[data-tga-sub-switch]');
  if (switchButton instanceof HTMLButtonElement && !switchButton.disabled) {
    const select = switchButton.closest('[data-tga-line-sub]')?.querySelector('[data-tga-sub-select]');
    const planId = Number(select?.value);
    if (planId) changeLine(switchButton, planId);
    return;
  }

  const revertButton = event.target.closest('[data-tga-sub-revert]');
  if (revertButton instanceof HTMLButtonElement && !revertButton.disabled) {
    changeLine(revertButton, null);
  }
});
